import { MaskedTextbox, SplitTextbox } from '@policygenius/mortar';
import { DateTime } from 'luxon';
import React, { useMemo, useState } from 'react';

import { FieldRenderProps } from 'forms/types';
import getErrorMessage from 'forms/utils/getErrorMessage';
import useEffectAfterMount from 'hooks/useEffectAfterMount';

import { getInitialDateValues } from '../DateField/utils';
import formStyles from '../form-inputs.module.scss';
import { ALL_INPUTS } from './constants';
import styles from './date-field.module.scss';
import { DateFieldProps, InputOptions } from './types';

type Props = DateFieldProps & FieldRenderProps<string, HTMLInputElement>;

const DateField: React.FC<Props> = ({
  input: { name, onBlur, onChange, onFocus, value },
  meta,
  id,
  includeDay,
  ...props
}) => {
  const initialValues = getInitialDateValues(meta.initial ? new Date(meta.initial) : null);
  const [values, setValues] = useState(initialValues);

  const usedId = id ?? name;

  useEffectAfterMount(() => {
    if (!values.month || !values.year || (includeDay && !values.day)) {
      if (!value) {
        return;
      }

      return onChange(null);
    }

    if (values.year.length < 4) {
      return onChange(null);
    }

    if (values.month === '0' || values.day === '0') {
      return onChange(null);
    }

    const date = DateTime.utc(
      Number(values.year),
      Number(values.month),
      includeDay ? Number(values.day) : 1
    );

    if (date.isValid) {
      return onChange(date.toISO());
    }

    return onChange(null);
  }, [includeDay, values]);

  const inputs = useMemo<InputOptions[]>(
    () => (includeDay ? ALL_INPUTS : ALL_INPUTS.filter(({ key }) => key !== 'day')),
    [includeDay]
  );

  return (
    <div className={formStyles['field-container']} data-cy={name}>
      <SplitTextbox {...props} id={usedId} errorMessage={getErrorMessage(meta)} data-cy={usedId}>
        {inputs.map((options) => (
          <MaskedTextbox
            key={options.key}
            className={styles.input}
            defaultValue={initialValues[options.key]}
            id={`${usedId}${options.label}`}
            inputMode="numeric"
            label={options.label}
            maskOptions={options.maskOptions}
            name={`${usedId}${options.label}`}
            onBlur={onBlur}
            onChange={({ unmasked }) => setValues((curr) => ({ ...curr, [options.key]: unmasked }))}
            onFocus={onFocus}
            placeholder={options.placeholder}
            type="text"
          />
        ))}
      </SplitTextbox>
    </div>
  );
};

export default DateField;
